import { Component, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FNFResponse } from '../../../models/response';
import { NgxSpinnerService } from 'ngx-spinner';
import { AuthService } from '../../../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [MatIcon, ReactiveFormsModule, CommonModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css',
})
export class ResetPasswordComponent {
  private fb = inject(FormBuilder);
  resetPasswordForm: FormGroup;
  errorMessage: string = '';
  ngxSpinnerService = inject(NgxSpinnerService);
  authService = inject(AuthService);
  toastr = inject(ToastrService);
  dialog = inject(MatDialog);
  router = inject(Router);
  showNewPassword = false;
  showConfirmPassword = false;
  currentYear = new Date().getFullYear();
  // Define character sets for password validation
  readonly uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  readonly lowercase = 'abcdefghijklmnopqrstuvwxyz';
  readonly numbers = '0123456789';
  readonly symbols = '!@#$%^&*';

  constructor() {
    this.resetPasswordForm = this.fb.group(
      {
        new_password: [
          '',
          [Validators.required, Validators.minLength(8), this.passwordValidator.bind(this)],
        ],
        confirm_password: ['', Validators.required],
      },
      { validators: this.matchPasswords('new_password', 'confirm_password') },
    );
  }

  // Custom password validator to check all requirements
  passwordValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const password = control.value;
    const errors: any = {};
    const missingRequirements: string[] = [];

    // Check for uppercase letters
    if (!/[A-Z]/.test(password)) {
      missingRequirements.push('uppercase letter');
    }

    // Check for lowercase letters
    if (!/[a-z]/.test(password)) {
      missingRequirements.push('lowercase letter');
    }

    // Check for numbers
    if (!/\d/.test(password)) {
      missingRequirements.push('number');
    }

    // Check for symbols
    if (!/[!@#$%^&*]/.test(password)) {
      missingRequirements.push('symbol (!@#$%^&*)');
    }

    if (missingRequirements.length > 0) {
      errors['passwordRequirements'] = {
        missing: missingRequirements,
      };
      return errors;
    }

    return null;
  }

  // Custom validator to check if new password matches confirm password
  matchPasswords(newPasswordKey: string, confirmPasswordKey: string) {
    return (formGroup: FormGroup) => {
      const newPassword = formGroup.controls[newPasswordKey];
      const confirmPassword = formGroup.controls[confirmPasswordKey];

      return newPassword.value === confirmPassword.value ? null : { passwordsMismatch: true };
    };
  }

  // Live checklist driving the requirements list + strength meter in the template
  get passwordChecks() {
    const value = this.resetPasswordForm.get('new_password')?.value || '';
    return {
      minLength: value.length >= 8,
      upper: /[A-Z]/.test(value),
      lower: /[a-z]/.test(value),
      number: /\d/.test(value),
      symbol: /[!@#$%^&*]/.test(value),
    };
  }

  get passwordStrengthScore(): number {
    return Object.values(this.passwordChecks).filter(Boolean).length;
  }

  get passwordStrengthLabel(): string {
    const score = this.passwordStrengthScore;
    if (score === 0) return '';
    if (score <= 2) return 'Weak';
    if (score <= 4) return 'Fair';
    return 'Strong';
  }

  // Helper method to get password validation error message
  getPasswordErrorMessage(): string {
    const passwordControl = this.resetPasswordForm.get('new_password');
    if (!passwordControl || !passwordControl.errors) {
      return '';
    }

    const errors = passwordControl.errors;

    if (errors['required']) {
      return 'New Password can not be empty.';
    }

    if (errors['minlength']) {
      return 'New Password must be at least 8 characters long.';
    }

    if (errors['passwordRequirements']) {
      const missing = errors['passwordRequirements'].missing;
      if (missing && missing.length > 0) {
        const missingList = missing.join(', ');
        return `Password must contain at least one ${missingList}.`;
      }
    }

    return 'Password does not meet the requirements.';
  }

  onSubmit() {
    if (this.resetPasswordForm.valid) {
      const formData = {
        password: this.resetPasswordForm.value.new_password,
      };
      this.resetPaswordNewUser(formData);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.resetPasswordForm.controls).forEach((key) => {
        this.resetPasswordForm.get(key)?.markAsTouched();
      });
    }
  }

  resetPaswordNewUser(password: any) {
    this.ngxSpinnerService.show();
    this.authService.resetPasswordUser(password).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.logoutConfirm();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  togglePasswordVisibility(field: string) {
    if (field === 'new') {
      this.showNewPassword = !this.showNewPassword;
    } else if (field === 'confirm') {
      this.showConfirmPassword = !this.showConfirmPassword;
    }
  }

  logoutConfirm() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message:
          'Your password has been reset successfully. This will logout the system and you will be redirected to login page.',
        title: 'Logout Confirmation',
        buttonText: {
          ok: 'Yes, Logout',
        },
      },
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.authService.logout();
        this.router.navigate(['/login']);
      }
    });
  }
}
